import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import type {
  Order,
  WebSocketMessage,
  WebSocketStatusType,
} from "../pages/monitors/types";
import { WebSocketStatus, isSameOrder } from "../pages/monitors/types";
import { getToken } from "../lib/auth";
import { handleAuthFailure } from "../lib/sessionRecovery";
import { useAppSelector } from "../store/hooks";
import { emitCloudNetworkError, getEdgeBaseUrl } from "../lib/edge/edgeState";
import { getEdgeTicketForToken } from "../lib/edge/edgeTicket";
import { mirrorStationOrders } from "../lib/edge/edgeMirror";
import {
  applyIncremental,
  isIncrementalMessage,
  isSnapshotMessage,
  replayOverSnapshot,
} from "./stationOrderEvents";

// Предпочитанието за звук в мониторите — пази се глобално, за да преживее refresh.
const SOUND_STORAGE_KEY = "monitor_sound_enabled";

// Кодът, с който BE-то затваря връзка с липсващ, изтекъл или подправен токен
// (`WS_1008_POLICY_VIOLATION`). Всяко друго затваряне е мрежово и се реконектва.
const WS_CLOSE_UNAUTHORIZED = 1008;

// Heartbeat към BE-то: сървърът затваря сокет без НИКАКВО клиентско съобщение
// в рамките на 75 s (WS_RECEIVE_TIMEOUT_SECONDS в bills/router.py) — така се
// чистят „сирачета" с жив TCP, но мъртъв JS. 25 s значи 3 шанса в прозореца.
// Кросрепо инвариант: смениш ли интервала, съобрази го с BE таймаута.
const HEARTBEAT_INTERVAL_MS = 25_000;

// След това число опити паузата спира да расте (експоненциално 1 s → 30 s) и
// реконектът продължава на всеки 30 s безкрайно — монитор, оставен без мрежа
// за час, трябва сам да се върне, не да чака ръчен reload.
//: Отказан или липсващ БИЛЕТ не се оправя с бърз реконект — оправя се с нов
//: билет, а той идва с връщането на облака. Затова рядък, но НЕспиращ опит:
//: кухненски екран няма кой да презареди.
const TICKET_RETRY_DELAY_MS = 30_000;
const RECONNECT_MAX_DELAY_MS = 30_000;

interface UseStationWebSocketProps {
  stationId: number | null;
  enabled?: boolean;
}

interface UseStationWebSocketReturn {
  orders: Order[];
  status: WebSocketStatusType;
  refresh: () => void;
  soundEnabled: boolean;
  // Звукът е включен (от localStorage), но аудиото още не е отключено с жест —
  // чака първи клик по екрана. Ползва се за „armed" визуализация на иконата.
  soundPending: boolean;
  enableSound: () => void;
}

// Токенът пътува през query, а не през Authorization хедър — браузърният
// WebSocket API не позволява custom хедъри при handshake.
// Локален режим (docs/edge.md): каналът е на главната каса
// (`ws://{ip}:{port}/edge/bills/ws/station/{id}?ticket=`) и носи билета за
// касата, не облачния JWT; без адрес или билет връзка няма (null).
const getWebSocketUrl = (
  stationId: number,
  token: string,
  edgeLocal: boolean,
): string | null => {
  if (edgeLocal) {
    const edgeBase = getEdgeBaseUrl();
    const ticket = getEdgeTicketForToken(token);
    if (!edgeBase || !ticket) return null;
    return `${edgeBase.replace(/^http/, "ws")}/bills/ws/station/${stationId}?ticket=${encodeURIComponent(ticket)}`;
  }
  const runtimeBase =
    typeof window !== "undefined" ? window.__ENV?.VITE_API_BASE_URL : undefined;
  const baseURL =
    runtimeBase ?? import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

  const wsUrl = baseURL.replace(/^http/, "ws");
  return `${wsUrl}/bills/ws/station/${stationId}?token=${encodeURIComponent(token)}`;
};


export const useStationWebSocket = ({
  stationId,
  enabled = true,
}: UseStationWebSocketProps): UseStationWebSocketReturn => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [status, setStatus] = useState<WebSocketStatusType>(
    WebSocketStatus.DISCONNECTED,
  );
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const heartbeatIntervalRef = useRef<number | null>(null);
  // Отложеното с един тик свързване при монтиране — виж ефекта най-долу.
  const pendingConnectRef = useRef<number | null>(null);
  // Вдига се от cleanup-а на ефекта: казва на onclose на ВЕЧЕ затворения сокет
  // да не планира реконект. Без този флаг cleanup затваряше сокета, но
  // запазеният onclose се стрелваше след демонтажа и създаваше нова връзка,
  // която никой не притежава — точно „сирачетата", които изяждаха connection
  // pool-а на бекенда.
  const disposedRef = useRef(false);
  // Токенът, с който е отворен ТЕКУЩИЯТ сокет — при 1008 трябва да знаем чий
  // токен е отхвърлен (WebSocket-ът няма config обект като axios заявките).
  const socketTokenRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // При общ акаунт валиден е Redux токенът, иначе този от localStorage —
  // същото правило като axios интерсептора в src/api/client.ts.
  const reduxToken = useAppSelector((state) => state.auth.token);
  // Локален режим → каналът е на касата; смяната на режима пресъздава сокета.
  const edgeLocal = useAppSelector((state) => state.edge.mode === "local");
  // Предпочитанието за звук се пази в localStorage, за да преживее refresh.
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem(SOUND_STORAGE_KEY) === "true",
  );
  const soundEnabledRef = useRef(soundEnabled);
  // Дали аудиото вече е отключено с потребителски жест в тази сесия.
  const [soundUnlocked, setSoundUnlocked] = useState(false);
  const isConnectingRef = useRef(false);

  // Синхронен близнак на `orders`. React прилага updater-ите чак при рендер, а
  // кадърът трябва да реши ВЕДНАГА дали поръчката вече е на екрана (звънецът).
  // Затова списъкът се смята върху рефа, рефът се записва пръв, а `setOrders`
  // само огледалва резултата. Всеки път, който променя списъка, минава оттук —
  // иначе рефът се разминава със state-а и следващото решение е върху стар списък.
  const ordersRef = useRef<Order[]>([]);
  // Станцията в реф, за да не сменя `applyOrders` самоличността си: тя е в
  // зависимостите на `connectWebSocket` и всяка нова функция там е нов сокет.
  const stationIdRef = useRef<number | null>(stationId);
  stationIdRef.current = stationId;
  // Бариерата пред снимката (stationOrderEvents.ts): колко снимки ТЕКУЩИЯТ
  // сокет още дължи (`initial_data` при отваряне + по една за всеки
  // изпратен „refresh") и кадрите, дошли, докато я чакаме. Нулират се с
  // всеки нов сокет — кадрите на стария не важат за снимката на новия.
  const pendingSnapshotsRef = useRef(0);
  const missedBeforeSnapshotRef = useRef<WebSocketMessage[]>([]);
  const applyOrders = useCallback((compute: (previous: Order[]) => Order[]) => {
    const next = compute(ordersRef.current);
    ordersRef.current = next;
    setOrders(next);
    // §4.1: докато има интернет, екранът се огледалва към касата. Касата не
    // може да си го построи сама — планът, от който се замразява (`GET
    // /bills`), не носи нито сметките, затворени до два часа назад, нито
    // сторнираните редове, а кухненският екран показва и двете. Без това при
    // замразяване поръчките на всичко затворено през последните два часа
    // изчезват от дъската и се връщат чак с интернета.
    // Огледалото НИКОГА не е причина кадърът да не стигне до екрана: то е
    // грижа за утрешния офлайн, а дъската е за сега (fail-open).
    const station = stationIdRef.current;
    if (station != null) {
      try {
        mirrorStationOrders({
          stationId: station,
          orders: next,
          ticket: getEdgeTicketForToken(socketTokenRef.current ?? reduxToken),
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.debug("[edge] station mirror skipped", error);
        }
      }
    }
  }, [reduxToken]);

  // Тук стоеше `clearOrders` — върнат от хука, но никога извикан. Не е закачен
  // при смяна на режима нарочно: в кухня празната дъска е ПО-ЛОШОТО зло от
  // стария списък (§5.3 — мъртъв канал изглежда точно като спокойна кухня и
  // готвачът просто спира да готви), а всяко успешно (пре)свързване и без това
  // презаписва целия списък с `initial_data`. Затова е махнат.

  const refresh = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // `refresh_data` е снимка като `initial_data` — същата бариера пред нея.
      pendingSnapshotsRef.current += 1;
      wsRef.current.send("refresh");
    }
  }, []);

  // Отключва аудиото (изисква потребителски жест заради autoplay политиката на
  // браузъра). Създава Audio-то и прави play→pause в рамките на жеста.
  const unlockAudio = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio("/notification.mp3");
      audioRef.current.play().catch(() => {});
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setSoundUnlocked(true);
  }, []);

  const enableSound = useCallback(() => {
    unlockAudio();
    const newValue = !soundEnabledRef.current;
    soundEnabledRef.current = newValue;
    setSoundEnabled(newValue);
    try {
      localStorage.setItem(SOUND_STORAGE_KEY, newValue ? "true" : "false");
    } catch {
      // ignore (напр. private mode)
    }
  }, [unlockAudio]);

  // Ако при зареждане звукът е бил включен (от localStorage), аудиото е заключено
  // до първи потребителски жест. Затова при ПЪРВИ клик където и да е го отключваме
  // автоматично, без да променяме състоянието. Ако звукът е изключен — нищо не правим.
  useEffect(() => {
    if (!soundEnabledRef.current || audioRef.current) return;
    const onFirstGesture = () => {
      unlockAudio();
      window.removeEventListener("pointerdown", onFirstGesture);
    };
    window.addEventListener("pointerdown", onFirstGesture);
    return () => window.removeEventListener("pointerdown", onFirstGesture);
  }, [unlockAudio]);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabledRef.current || !audioRef.current) return;

    try {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        console.error("Audio play error:", err);
      });
    } catch (error) {
      console.error("Failed to play notification sound:", error);
    }
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Праща "ping" на интервал, докато връзката е отворена — виж коментара към
  // HEARTBEAT_INTERVAL_MS. Само едно живо аудио: старият интервал се спира.
  const startHeartbeat = useCallback(
    (ws: WebSocket) => {
      stopHeartbeat();
      heartbeatIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send("ping");
        }
      }, HEARTBEAT_INTERVAL_MS);
    },
    [stopHeartbeat],
  );

  const connectWebSocket = useCallback(() => {
    if (!stationId || !enabled) return;

    // Предотвратяваме множествени връзки
    if (
      isConnectingRef.current ||
      wsRef.current?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    const token = getToken(reduxToken);
    if (!token) {
      // Нито служителски, нито терминален токен — устройството няма самоличност.
      void handleAuthFailure({ source: "ws-no-token" });
      return;
    }
    socketTokenRef.current = token;

    const wsUrl = getWebSocketUrl(stationId, token, edgeLocal);
    if (!wsUrl) {
      // Локален режим без адрес на касата или без билет — няма към какво да се
      // вържем. Ефектът се преизпълнява при нов билет (ПИН вход) или режим, но
      // кухненски екран не се докосва от никого: пробваме пак и сами.
      setStatus(WebSocketStatus.ERROR);
      if (enabled && !disposedRef.current) {
        reconnectTimeoutRef.current = window.setTimeout(
          () => connectWebSocket(),
          TICKET_RETRY_DELAY_MS,
        );
      }
      return;
    }

    try {
      // Създаване на WebSocket
      isConnectingRef.current = true;
      setStatus(WebSocketStatus.CONNECTING);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      pendingSnapshotsRef.current = 1;
      missedBeforeSnapshotRef.current = [];

      ws.onopen = () => {
        // Стар сокет, изместен от по-нов (или демонтиран компонент) — не му е
        // работа да пипа общото състояние.
        if (wsRef.current !== ws || disposedRef.current) return;
        setStatus(WebSocketStatus.CONNECTED);
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
        startHeartbeat(ws);
      };

      ws.onmessage = (event) => {
        // Стар сокет, изместен от по-нов (или демонтиран компонент): кадър по
        // него НЕ пипа общото състояние. Същият пазач, който onopen и onclose
        // имат отдавна — onmessage беше единственият без него и закъснял кадър
        // по затварящата се връзка се прилагаше върху новото състояние.
        if (wsRef.current !== ws || disposedRef.current) return;
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          if (isSnapshotMessage(message)) {
            // Снимката заменя списъка, но кадрите, които я изпревариха, се
            // прилагат пак върху нея — иначе тя заличава поръчка, показана
            // секунда по-рано (stationOrderEvents.ts). Всички изпреварили
            // кадри са отразени в СЛЕДВАЩА снимка: сървърът чете „refresh"
            // чак след като е изпратил предишната. Затова опашката се
            // изпразва при всяка снимка, дори ако чакаме още една.
            const missed = missedBeforeSnapshotRef.current;
            missedBeforeSnapshotRef.current = [];
            pendingSnapshotsRef.current = Math.max(
              0,
              pendingSnapshotsRef.current - 1,
            );
            const snapshot = message.data as Order[];
            applyOrders(() => replayOverSnapshot(snapshot, missed));
            return;
          }

          if (!isIncrementalMessage(message)) return;
          if (pendingSnapshotsRef.current > 0) {
            missedBeforeSnapshotRef.current.push(message);
          }

          // Повторна доставка на ВЕЧЕ показана поръчка не е нова поръчка —
          // вторият звънец беше част от „мазалото", което кухнята чуваше.
          // Решава се срещу рефа ПРЕДИ прилагането (виж `ordersRef`).
          const ringsBell =
            message.type === "new_order" &&
            !ordersRef.current.some((order) =>
              isSameOrder(order, message.data as Order),
            );
          applyOrders((previous) => applyIncremental(previous, message));
          if (ringsBell) playNotificationSound();
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      // ws.onerror = (error) => {
      //   console.error("WebSocket error:", error);
      //   setStatus(WebSocketStatus.ERROR);
      // };

      ws.onerror = () => {
        // Игнорираме грешки ако връзката се затваря нормално (cleanup)
        if (
          ws.readyState === WebSocket.CLOSING ||
          ws.readyState === WebSocket.CLOSED
        ) {
          return;
        }
        setStatus(WebSocketStatus.ERROR);
      };

      ws.onclose = (event) => {
        // Затваря се СТАР сокет (изместен от по-нов) — новият е си жив,
        // не нулираме референцията към него и не планираме реконект.
        // Преди този guard старият onclose зануляваше wsRef безусловно и
        // следващият connectWebSocket създаваше ДУБЛИРАНА връзка.
        if (wsRef.current !== ws) return;

        stopHeartbeat();
        setStatus(WebSocketStatus.DISCONNECTED);
        wsRef.current = null;
        isConnectingRef.current = false;

        // Компонентът е демонтиран/ефектът е презареден — cleanup-ът е
        // затворил сокета нарочно. Реконект оттук би създал връзка-сирак.
        if (disposedRef.current) return;

        // Токенът е отхвърлен от сървъра — реконектът е безсмислен, всеки
        // следващ опит носи същия токен и получава същия отговор. Решението
        // „до ПИН или пълен изход" е СЪЩОТО като при 401 по HTTP и живее на
        // едно място (lib/sessionRecovery.ts). Тук по-рано стоеше собствен
        // clearToken() + редирект — втори кладенец за същия срив, който триеше
        // общия за всички прозорци localStorage.
        if (event.code === WS_CLOSE_UNAUTHORIZED) {
          // Отхвърлен БИЛЕТ от касата (локален режим) не е присъда за
          // сесията — облакът, който би я проверил, точно сега го няма
          // (docs/edge.md). Но и мълчание не е отговор: кухненски екран, чийто
          // билет е изтекъл през нощта, иначе остава мъртъв до ръчно
          // презареждане, а на кухнята няма кой да го забележи. Затова бавен
          // повторен опит — билетът се сменя сам, щом облакът се върне.
          if (!edgeLocal) {
            void handleAuthFailure({
              failedToken: socketTokenRef.current,
              source: "ws-1008",
            });
            return;
          }
          setStatus(WebSocketStatus.ERROR);
          if (enabled && !disposedRef.current) {
            reconnectTimeoutRef.current = window.setTimeout(
              () => connectWebSocket(),
              TICKET_RETRY_DELAY_MS,
            );
          }
          return;
        }

        // Затворен облачен сокет е подсказка за lib/edge/edgeMode.ts да
        // сондира облака — мониторът може да няма нито една HTTP заявка в полет.
        if (!edgeLocal) emitCloudNetworkError();

        if (enabled) {
          const delay = Math.min(
            1000 * Math.pow(2, Math.min(reconnectAttemptsRef.current, 10)),
            RECONNECT_MAX_DELAY_MS,
          );

          // Един чакащ реконект наведнъж — старият таймер се отменя, иначе
          // презаписаният ref го правеше неотменим и той пак палеше връзка.
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            reconnectAttemptsRef.current++;
            connectWebSocket();
          }, delay);
        }
      };
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      setStatus(WebSocketStatus.ERROR);
      isConnectingRef.current = false;
    }
  }, [
    stationId,
    enabled,
    reduxToken,
    edgeLocal,
    applyOrders,
    playNotificationSound,
    startHeartbeat,
    stopHeartbeat,
  ]);

  // Свързването е отложено с ЕДИН тик нарочно (§16.4 №4 на договора,
  // docs/edge.md §Капани). React вдига ефектите при монтиране по два пъти
  // (setup → cleanup → setup, StrictMode), а сокет, отворен в първия setup,
  // cleanup-ът затваря веднага: измерено — 12 връзки за 6 станции, от които 6
  // умират, преди да получат `initial_data`, а бекендът/касата плащат двойно
  // за всеки монитор. Отложеният опит се отменя от cleanup-а, преди изобщо да
  // е стигнал до `new WebSocket`, така че остава по ЕДНА връзка на станция.
  // Реконектът след затваряне минава по своя път (onclose) и не се отлага тук.
  useEffect(() => {
    disposedRef.current = false;
    if (stationId && enabled) {
      pendingConnectRef.current = window.setTimeout(() => {
        pendingConnectRef.current = null;
        connectWebSocket();
      }, 0);
    }

    return () => {
      // Редът е важен: първо казваме на всички закъснели handler-и, че този
      // живот на ефекта приключи, чак после затваряме. Иначе onclose на
      // затваряния сокет планираше реконект СЛЕД демонтажа и оставяше
      // сирак-връзка, която държи ресурси на бекенда.
      disposedRef.current = true;
      if (pendingConnectRef.current !== null) {
        clearTimeout(pendingConnectRef.current);
        pendingConnectRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      stopHeartbeat();
      reconnectAttemptsRef.current = 0;
      isConnectingRef.current = false;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [stationId, enabled, connectWebSocket, stopHeartbeat]);

  return useMemo(
    () => ({
      orders,
      status,
      refresh,
      soundEnabled,
      soundPending: soundEnabled && !soundUnlocked,
      enableSound,
    }),
    [orders, status, refresh, soundEnabled, soundUnlocked, enableSound],
  );
};
